/**
 * SIMPLIFIED Utility functions for transforming between wizard state and enhanced API format
 */
import type {
	AuthorizationFeeMode,
	DiscountRateMode,
	EnhancedCustomRatesRequest,
	EnhancedCustomRatesResponse,
	PricingConfiguration,
	TieredRateStructure,
	UnifiedRateConfig,
} from '../types';

// Helper constants for card types and networks
const ALL_CARD_TYPES = [
	'VISA',
	'MASTERCARD',
	'DISCOVER',
	'AMERICAN_EXPRESS',
	'VISA_DEBIT',
	'MASTERCARD_DEBIT',
	'DISCOVER_DEBIT',
	'PIN_DEBIT',
	'EBT',
	'FLEET',
];
const ALL_NETWORKS = [
	'VISA',
	'MASTERCARD',
	'DISCOVER',
	'AMERICAN_EXPRESS',
	'VISA_DEBIT',
	'MASTERCARD_DEBIT',
	'DISCOVER_DEBIT',
	'PIN_DEBIT',
];

/**
 * Helper function to extract uniform value from a collection of rates/fees
 * Returns the value if all non-null values are the same (within tolerance), otherwise undefined
 */
function getUniformValue(
	collection:
		| Record<
				string,
				{ discount_rate?: number; authorization_fee?: number } | undefined
		  >
		| undefined,
	keys: string[],
	valueKey: 'discount_rate' | 'authorization_fee',
): number | undefined {
	if (!collection) {
		return undefined;
	}

	const allValues: number[] = keys
		.map((key) => collection[key]?.[valueKey])
		.filter((value): value is number => value !== null && value !== undefined);

	if (allValues.length === 0) {
		return undefined;
	}

	const firstValue = allValues[0];
	const allSame = allValues.every(
		(value) => Math.abs(value - firstValue) < 0.001,
	);

	return allSame ? firstValue : undefined;
}

/**
 * Transform wizard state to enhanced API request - SIMPLIFIED DIRECT MAPPING
 */
export function transformWizardToEnhancedRequest(
	config: PricingConfiguration,
): EnhancedCustomRatesRequest {
	const result = {
		name: config.name,
		description: config.description,
		is_default: config.is_default,
		// SURCHARGE is a proposal plan, not a statement type, so the API's
		// rate_plan_type enum has no value for it; selected_rate_types carries it.
		// '' clears the value on update.
		partner_id: config.partner_id ?? '',
		risk_tier: config.risk_tier ?? '',
		status: config.status ?? 'CUSTOM',
		rate_plan_type:
			config.selected_rate_types?.[0] === 'SURCHARGE'
				? 'IC_PLUS'
				: config.selected_rate_types && config.selected_rate_types.length > 0
					? (config.selected_rate_types[0] as any)
					: config.rate_plan_type || 'IC_PLUS',
		selected_rate_types: config.selected_rate_types || [],
		pricing_mode: {
			discount_rate_mode: getDiscountRateMode(config) as DiscountRateMode,
			authorization_fee_mode: getAuthorizationFeeMode(
				config,
			) as AuthorizationFeeMode,
			pass_through_cost_mode: config.pass_through_cost_mode,
		},
		rates: buildSimpleRatesConfig(config) as any,
		pass_through_costs: {
			use_custom_assessment_rates: !config.use_standard_assessment_rates,
			custom_assessment_rate: config.custom_assessment_rate,
			use_bundled_amex_rate: config.use_bundled_amex_rate,
			bundled_amex_rate: config.bundled_amex_rate,
			pass_through_card_brand_fees: config.pass_through_card_brand_fees,
			pass_through_domestic_card_brand_fees:
				config.pass_through_domestic_card_brand_fees,
			pass_through_international_card_brand_fees:
				config.pass_through_international_card_brand_fees,
		},
		monthly_and_batch_fees:
			config.monthly_fees ||
			config.batch_fee ||
			config.gateway_fee ||
			config.equipment_fee
				? {
						monthly_fees: (config.monthly_fees || []).map((fee) => ({
							label: fee.label,
							// Convert string amounts to numbers for API validation
							amount:
								typeof fee.amount === 'string'
									? parseFloat(fee.amount) || 0
									: fee.amount || 0,
						})),
						// Convert string batch_fee to number for API validation
						batch_fee:
							typeof config.batch_fee === 'string'
								? parseFloat(config.batch_fee) || 0
								: config.batch_fee || 0,
						batch_count_per_month: config.batch_count_per_month || 30,
						gateway_fee: Number(config.gateway_fee) || 0,
						equipment_fee: Number(config.equipment_fee) || 0,
					}
				: undefined,
		// Flat tiered specific authorization fee mode (stored separately for multi-rate-type scenarios)
		flat_tiered_authorization_fee_mode: config.selected_rate_types?.includes(
			'FLAT_TIERED',
		)
			? config.flat_tiered_authorization_fee_mode
			: undefined,
		pricing_limits: config.pricing_limits,
	};

	return result;
}

/**
 * Get the appropriate authorization fee mode based on selected rate types
 */
function getAuthorizationFeeMode(config: PricingConfiguration): string {
	const selectedTypes = config.selected_rate_types || [];

	// Check if any selected rate type has different_by_transaction_type
	if (
		selectedTypes.includes('IC_PLUS') &&
		config.ic_plus_authorization_fee_mode === 'different_by_transaction_type'
	) {
		return 'different_by_transaction_type';
	}
	if (
		selectedTypes.includes('TIERED') &&
		config.tiered_authorization_fee_mode === 'different_by_transaction_type'
	) {
		return 'different_by_transaction_type';
	}
	if (
		selectedTypes.includes('FLAT_TIERED') &&
		config.flat_tiered_authorization_fee_mode ===
			'different_by_transaction_type'
	) {
		return 'different_by_transaction_type';
	}

	// Default fallback
	return config.authorization_fee_mode || 'same_for_all';
}

/**
 * Get the appropriate discount rate mode based on selected rate types
 */
function getDiscountRateMode(config: PricingConfiguration): string {
	const selectedTypes = config.selected_rate_types || [];

	// Check if any selected rate type requires different_by_card_type
	if (
		selectedTypes.includes('IC_PLUS') &&
		config.ic_plus_card_specific_discount_rates
	) {
		return 'different_by_card_type';
	}
	if (
		selectedTypes.includes('TIERED') &&
		config.tiered_card_specific_discount_rates
	) {
		return 'different_by_card_type';
	}
	if (
		selectedTypes.includes('FLAT_TIERED') &&
		config.flat_tiered_card_specific_discount_rates
	) {
		return 'different_by_card_type';
	}

	// Default fallback
	return config.discount_rate_mode || 'same_for_all';
}

/**
 * UNIFIED: Build rates config from simplified UI state
 */
function buildSimpleRatesConfig(config: PricingConfiguration) {
	const selectedTypes = config.selected_rate_types || [];

	return {
		// IC_PLUS uses mode + same_for_all/by_card_type structure
		standard_rates: selectedTypes.includes('IC_PLUS')
			? buildICPlusRatesConfig(config)
			: undefined,
		// TIERED uses existing buildSimpleTieredRatesConfig (unchanged)
		tiered_rates: selectedTypes.includes('TIERED')
			? buildSimpleTieredRatesConfig(config)
			: undefined,
		// FLAT_TIERED uses by_network structure
		flat_tiered_rates: selectedTypes.includes('FLAT_TIERED')
			? buildFlatTieredRatesConfig(config)
			: undefined,
		surcharge_rates: selectedTypes.includes('SURCHARGE')
			? config.surcharge_rates
			: undefined,
	};
}

/**
 * SIMPLIFIED: Build tiered rates config directly from UI state
 */
function buildSimpleTieredRatesConfig(config: PricingConfiguration) {
	// Use tiered-specific mode, falling back to global mode
	const tieredDiscountRateMode =
		config.tiered_discount_rate_mode ||
		config.discount_rate_mode ||
		'same_for_all';
	const tieredSameAsCredit =
		config.tiered_same_as_credit_6tier ?? config.same_as_credit_6tier;

	// FIX: Only use same_for_all structure when mode is explicitly same_for_all
	// "Same as above" checkbox should NOT change the mode - it only affects rate copying within the mode
	if (tieredDiscountRateMode === 'same_for_all') {
		// Direct mapping from UI state
		const creditRates: TieredRateStructure = {
			qualified_rate: config.credit_rates?.qualified_rate || 1.65,
			qualified_per_item: config.credit_rates?.qualified_per_item || 0.91,
			midqualified_rate: config.credit_rates?.midqualified_rate || 1.75,
			midqualified_per_item: config.credit_rates?.midqualified_per_item || 0.91,
			nonqualified_rate: config.credit_rates?.nonqualified_rate || 2.25,
			nonqualified_per_item: config.credit_rates?.nonqualified_per_item || 0.91,
		};

		const debitRates: TieredRateStructure = {
			qualified_rate: config.debit_rates?.qualified_rate || 1.6,
			qualified_per_item: config.debit_rates?.qualified_per_item || 0.79,
			midqualified_rate: config.debit_rates?.midqualified_rate || 1.7,
			midqualified_per_item: config.debit_rates?.midqualified_per_item || 0.79,
			nonqualified_rate: config.debit_rates?.nonqualified_rate || 2.2,
			nonqualified_per_item: config.debit_rates?.nonqualified_per_item || 0.79,
		};

		return {
			mode: 'same_for_all',
			same_for_all: {
				credit_rates: creditRates,
				debit_rates: debitRates,
				same_as_credit: tieredSameAsCredit ?? true,
			},
		};
	} else {
		// Different by card type - build individual card rates
		// Build rates dictionary for new structure
		const rates: Record<string, any> = {};

		// VISA, MASTERCARD, DISCOVER - use individual card rates if available
		for (const cardType of ['VISA', 'MASTERCARD', 'DISCOVER']) {
			// Get individual card rates or fallback to generic rates

			const creditOnlyRates = config.credit_rates || {
				qualified_rate: 1.65,
				qualified_per_item: 0.91,
				midqualified_rate: 1.75,
				midqualified_per_item: 0.91,
				nonqualified_rate: 2.25,
				nonqualified_per_item: 0.91,
			};

			const withDebitRates = tieredSameAsCredit
				? creditOnlyRates
				: config.debit_rates || {
						qualified_rate: 1.6,
						qualified_per_item: 0.79,
						midqualified_rate: 1.7,
						midqualified_per_item: 0.79,
						nonqualified_rate: 2.2,
						nonqualified_per_item: 0.79,
					};

			rates[cardType] = {
				credit_only: creditOnlyRates,
				with_debit: withDebitRates,
			};
		}

		// AMERICAN_EXPRESS

		const amexRates =
			config.amex_rates && Object.keys(config.amex_rates).length > 0
				? config.amex_rates
				: {
						qualified_rate: 1.85,
						qualified_per_item: 0.1,
						midqualified_rate: 2.5,
						midqualified_per_item: 0.1,
						nonqualified_rate: 2.85,
						nonqualified_per_item: 0.1,
					};

		rates.AMERICAN_EXPRESS = {
			tiered: amexRates,
		};

		// Individual debit cards - use individual rates if available, otherwise use generic debit rates

		const visaDebitRates =
			config.visa_debit_rates && Object.keys(config.visa_debit_rates).length > 0
				? config.visa_debit_rates
				: config.debit_rates || {
						qualified_rate: 1.6,
						qualified_per_item: 0.79,
						midqualified_rate: 1.7,
						midqualified_per_item: 0.79,
						nonqualified_rate: 2.2,
						nonqualified_per_item: 0.79,
					};

		rates.VISA_DEBIT = {
			tiered: visaDebitRates,
		};

		const mastercardDebitRates =
			config.mastercard_debit_rates &&
			Object.keys(config.mastercard_debit_rates).length > 0
				? config.mastercard_debit_rates
				: config.debit_rates || {
						qualified_rate: 1.6,
						qualified_per_item: 0.79,
						midqualified_rate: 1.7,
						midqualified_per_item: 0.79,
						nonqualified_rate: 2.2,
						nonqualified_per_item: 0.79,
					};

		rates.MASTERCARD_DEBIT = {
			tiered: mastercardDebitRates,
		};

		const discoverDebitRates =
			config.discover_debit_rates &&
			Object.keys(config.discover_debit_rates).length > 0
				? config.discover_debit_rates
				: config.debit_rates || {
						qualified_rate: 1.6,
						qualified_per_item: 0.79,
						midqualified_rate: 1.7,
						midqualified_per_item: 0.79,
						nonqualified_rate: 2.2,
						nonqualified_per_item: 0.79,
					};

		rates.DISCOVER_DEBIT = {
			tiered: discoverDebitRates,
		};

		const pinDebitRates =
			config.pin_debit_rates && Object.keys(config.pin_debit_rates).length > 0
				? config.pin_debit_rates
				: config.debit_rates || {
						qualified_rate: 1.85,
						qualified_per_item: 0.1,
						midqualified_rate: 2.5,
						midqualified_per_item: 0.1,
						nonqualified_rate: 2.85,
						nonqualified_per_item: 0.1,
					};

		rates.PIN_DEBIT = {
			tiered: pinDebitRates,
		};

		return {
			mode: 'different_by_card_type',
			by_card_type: rates,
			// FIX: Include same_as_credit at top level to persist regardless of mode
			same_as_credit: tieredSameAsCredit ?? false,
		};
	}
}

/**
 * Build IC_PLUS rates config with correct mode structure
 * Produces format: { mode: 'same_for_all', same_for_all: {...} } or { mode: 'different_by_card_type', by_card_type: {...} }
 *
 * Updated to read from rate-type-specific fields first, then fallback to rate_config
 */
function buildICPlusRatesConfig(config: PricingConfiguration) {
	// Check rate-type-specific modes first, then fallback to rate_config
	const discountRateMode =
		config.ic_plus_discount_rate_mode ||
		config.rate_config?.mode ||
		'same_for_all';
	const authFeeMode =
		config.ic_plus_authorization_fee_mode ||
		config.authorization_fee_mode ||
		'same_for_all';

	// Handle mixed modes: same discount rate + different auth fees
	if (discountRateMode === 'same_for_all' && authFeeMode === 'same_for_all') {
		// Same rates for all networks - check rate-type-specific fields first
		const discountRate =
			config.ic_plus_standard_discount_rate ??
			config.rate_config?.standardRate ??
			0.65;
		const authFee =
			config.ic_plus_standard_authorization_fee ??
			config.rate_config?.standardAuthFee ??
			0.1;

		return {
			mode: 'same_for_all',
			same_for_all: {
				discount_rate: discountRate,
				authorization_fee: authFee,
			},
		};
	} else if (
		discountRateMode === 'same_for_all' &&
		authFeeMode === 'different_by_transaction_type'
	) {
		// Same discount rate but different auth fees - use different_by_card_type structure
		const discountRate =
			config.ic_plus_standard_discount_rate ??
			config.rate_config?.standardRate ??
			0.65;
		const transactionFees =
			config.ic_plus_transaction_specific_fees ||
			config.transaction_specific_fees;

		const rates: Record<string, any> = {};
		for (const cardType of [
			'VISA',
			'MASTERCARD',
			'DISCOVER',
			'AMERICAN_EXPRESS',
			'VISA_DEBIT',
			'MASTERCARD_DEBIT',
			'DISCOVER_DEBIT',
			'PIN_DEBIT',
			'EBT',
			'FLEET',
		]) {
			rates[cardType] = {
				discount_rate: discountRate,
				authorization_fee: getAuthFeeForCardType(cardType, config),
			};
		}

		return {
			mode: 'different_by_card_type',
			by_card_type: rates,
		};
	} else if (
		discountRateMode === 'different_by_card_type' &&
		authFeeMode === 'same_for_all'
	) {
		// FIX: Different discount rates but SAME auth fee for all cards
		// This was the bug - the old code ignored authFeeMode and used per-card-type auth fees
		const cardRates = config.ic_plus_card_specific_discount_rates;
		const rateConfig = config.rate_config;
		// Use the standard auth fee for ALL cards when authFeeMode is 'same_for_all'
		const standardAuthFee =
			config.ic_plus_standard_authorization_fee ??
			config.rate_config?.standardAuthFee ??
			0.1;

		return {
			mode: 'different_by_card_type',
			by_card_type: {
				VISA: {
					discount_rate:
						cardRates?.visa_mastercard_discover_credit ??
						rateConfig?.visa_mastercard_discover_credit?.discountRate ??
						0.65,
					authorization_fee: standardAuthFee,
				},
				MASTERCARD: {
					discount_rate:
						cardRates?.visa_mastercard_discover_credit ??
						rateConfig?.visa_mastercard_discover_credit?.discountRate ??
						0.65,
					authorization_fee: standardAuthFee,
				},
				DISCOVER: {
					discount_rate:
						cardRates?.visa_mastercard_discover_credit ??
						rateConfig?.visa_mastercard_discover_credit?.discountRate ??
						0.65,
					authorization_fee: standardAuthFee,
				},
				AMERICAN_EXPRESS: {
					discount_rate:
						cardRates?.american_express ??
						rateConfig?.american_express?.discountRate ??
						0.7,
					authorization_fee: standardAuthFee,
				},
				VISA_DEBIT: {
					discount_rate:
						cardRates?.visa_mastercard_discover_debit ??
						rateConfig?.visa_mastercard_discover_debit?.discountRate ??
						0.45,
					authorization_fee: standardAuthFee,
				},
				MASTERCARD_DEBIT: {
					discount_rate:
						cardRates?.visa_mastercard_discover_debit ??
						rateConfig?.visa_mastercard_discover_debit?.discountRate ??
						0.45,
					authorization_fee: standardAuthFee,
				},
				DISCOVER_DEBIT: {
					discount_rate:
						cardRates?.visa_mastercard_discover_debit ??
						rateConfig?.visa_mastercard_discover_debit?.discountRate ??
						0.45,
					authorization_fee: standardAuthFee,
				},
				PIN_DEBIT: {
					discount_rate:
						cardRates?.pin_debit ?? rateConfig?.pin_debit?.discountRate ?? 0.55,
					authorization_fee: standardAuthFee,
				},
			},
		};
	} else {
		// Different discount rates AND different auth fees per transaction type
		const cardRates = config.ic_plus_card_specific_discount_rates;
		const rateConfig = config.rate_config;
		const transactionFees =
			config.ic_plus_transaction_specific_fees ||
			config.transaction_specific_fees;

		return {
			mode: 'different_by_card_type',
			by_card_type: {
				VISA: {
					discount_rate:
						cardRates?.visa_mastercard_discover_credit ??
						rateConfig?.visa_mastercard_discover_credit?.discountRate ??
						0.65,
					authorization_fee:
						transactionFees?.credit_transactions ??
						rateConfig?.visa_mastercard_discover_credit?.authFee ??
						0.1,
				},
				MASTERCARD: {
					discount_rate:
						cardRates?.visa_mastercard_discover_credit ??
						rateConfig?.visa_mastercard_discover_credit?.discountRate ??
						0.65,
					authorization_fee:
						transactionFees?.credit_transactions ??
						rateConfig?.visa_mastercard_discover_credit?.authFee ??
						0.1,
				},
				DISCOVER: {
					discount_rate:
						cardRates?.visa_mastercard_discover_credit ??
						rateConfig?.visa_mastercard_discover_credit?.discountRate ??
						0.65,
					authorization_fee:
						transactionFees?.credit_transactions ??
						rateConfig?.visa_mastercard_discover_credit?.authFee ??
						0.1,
				},
				AMERICAN_EXPRESS: {
					discount_rate:
						cardRates?.american_express ??
						rateConfig?.american_express?.discountRate ??
						0.7,
					authorization_fee:
						transactionFees?.credit_transactions ??
						rateConfig?.american_express?.authFee ??
						0.15,
				},
				VISA_DEBIT: {
					discount_rate:
						cardRates?.visa_mastercard_discover_debit ??
						rateConfig?.visa_mastercard_discover_debit?.discountRate ??
						0.45,
					authorization_fee:
						transactionFees?.debit_transactions ??
						rateConfig?.visa_mastercard_discover_debit?.authFee ??
						0.08,
				},
				MASTERCARD_DEBIT: {
					discount_rate:
						cardRates?.visa_mastercard_discover_debit ??
						rateConfig?.visa_mastercard_discover_debit?.discountRate ??
						0.45,
					authorization_fee:
						transactionFees?.debit_transactions ??
						rateConfig?.visa_mastercard_discover_debit?.authFee ??
						0.08,
				},
				DISCOVER_DEBIT: {
					discount_rate:
						cardRates?.visa_mastercard_discover_debit ??
						rateConfig?.visa_mastercard_discover_debit?.discountRate ??
						0.45,
					authorization_fee:
						transactionFees?.debit_transactions ??
						rateConfig?.visa_mastercard_discover_debit?.authFee ??
						0.08,
				},
				PIN_DEBIT: {
					discount_rate:
						cardRates?.pin_debit ?? rateConfig?.pin_debit?.discountRate ?? 0.55,
					authorization_fee:
						transactionFees?.pin_debit_transactions ??
						rateConfig?.pin_debit?.authFee ??
						0.12,
				},
			},
		};
	}
}

/**
 * Build FLAT_TIERED rates config with by_network structure
 * Produces format: { by_network: { VISA: {...}, MASTERCARD: {...}, ... } }
 */
function buildFlatTieredRatesConfig(config: PricingConfiguration) {
	// Check rate-type-specific modes first, then fallback to rate_config
	const discountRateMode =
		config.flat_tiered_discount_rate_mode ||
		config.rate_config?.mode ||
		'same_for_all';
	const authFeeMode =
		config.flat_tiered_authorization_fee_mode ||
		config.authorization_fee_mode ||
		'same_for_all';
	const rateConfig = config.rate_config;

	// Handle mixed modes: same discount rate + different auth fees
	if (discountRateMode === 'same_for_all' && authFeeMode === 'same_for_all') {
		// All networks/cards get the same rates - check rate-type-specific fields first
		const rate =
			config.flat_tiered_standard_discount_rate ??
			rateConfig?.standardRate ??
			2.9;
		const authFee =
			config.flat_tiered_standard_authorization_fee ??
			rateConfig?.standardAuthFee ??
			0.3;

		return {
			by_network: {
				VISA: { discount_rate: rate, authorization_fee: authFee },
				MASTERCARD: { discount_rate: rate, authorization_fee: authFee },
				DISCOVER: { discount_rate: rate, authorization_fee: authFee },
				AMERICAN_EXPRESS: { discount_rate: rate, authorization_fee: authFee },
				VISA_DEBIT: { discount_rate: rate, authorization_fee: authFee },
				MASTERCARD_DEBIT: { discount_rate: rate, authorization_fee: authFee },
				DISCOVER_DEBIT: { discount_rate: rate, authorization_fee: authFee },
				PIN_DEBIT: { discount_rate: rate, authorization_fee: authFee },
			},
		};
	} else if (
		discountRateMode === 'same_for_all' &&
		authFeeMode === 'different_by_transaction_type'
	) {
		// Same discount rate but different auth fees - use by_network structure with different fees
		const rate =
			config.flat_tiered_standard_discount_rate ??
			rateConfig?.standardRate ??
			2.9;
		const transactionFees =
			config.flat_tiered_transaction_specific_fees ||
			config.transaction_specific_fees;

		return {
			by_network: {
				VISA: {
					discount_rate: rate,
					authorization_fee: transactionFees?.credit_transactions ?? 0.3,
				},
				MASTERCARD: {
					discount_rate: rate,
					authorization_fee: transactionFees?.credit_transactions ?? 0.3,
				},
				DISCOVER: {
					discount_rate: rate,
					authorization_fee: transactionFees?.credit_transactions ?? 0.3,
				},
				AMERICAN_EXPRESS: {
					discount_rate: rate,
					authorization_fee: transactionFees?.credit_transactions ?? 0.3,
				},
				VISA_DEBIT: {
					discount_rate: rate,
					authorization_fee: transactionFees?.debit_transactions ?? 0.3,
				},
				MASTERCARD_DEBIT: {
					discount_rate: rate,
					authorization_fee: transactionFees?.debit_transactions ?? 0.3,
				},
				DISCOVER_DEBIT: {
					discount_rate: rate,
					authorization_fee: transactionFees?.debit_transactions ?? 0.3,
				},
				PIN_DEBIT: {
					discount_rate: rate,
					authorization_fee: transactionFees?.pin_debit_transactions ?? 0.3,
				},
			},
		};
	} else if (
		discountRateMode === 'different_by_card_type' &&
		authFeeMode === 'same_for_all'
	) {
		// Combo 3: Different discount rates per card, but SAME auth fee for all
		const standardAuthFee =
			config.flat_tiered_standard_authorization_fee ??
			rateConfig?.standardAuthFee ??
			0.3;
		const cardRates = config.flat_tiered_card_specific_discount_rates;

		// Get rates from card-specific config or fallback to rate_config
		const creditRate =
			cardRates?.visa_mastercard_discover_credit ??
			rateConfig?.visa_mastercard_discover_credit?.discountRate ??
			2.9;
		const debitRate =
			cardRates?.visa_mastercard_discover_debit ??
			rateConfig?.visa_mastercard_discover_debit?.discountRate ??
			2.9;
		const amexRate =
			cardRates?.american_express ??
			rateConfig?.american_express?.discountRate ??
			3.75;
		const pinDebitRate =
			cardRates?.pin_debit ?? rateConfig?.pin_debit?.discountRate ?? 2.9;

		return {
			by_network: {
				// Credit networks use credit rates, debit networks use debit rates, all with same auth fee
				...Object.fromEntries(
					['VISA', 'MASTERCARD', 'DISCOVER'].map((network) => [
						network,
						{ discount_rate: creditRate, authorization_fee: standardAuthFee },
					]),
				),
				...Object.fromEntries(
					['VISA_DEBIT', 'MASTERCARD_DEBIT', 'DISCOVER_DEBIT'].map(
						(network) => [
							network,
							{ discount_rate: debitRate, authorization_fee: standardAuthFee },
						],
					),
				),
				AMERICAN_EXPRESS: {
					discount_rate: amexRate,
					authorization_fee: standardAuthFee,
				},
				PIN_DEBIT: {
					discount_rate: pinDebitRate,
					authorization_fee: standardAuthFee,
				},
			},
		};
	} else {
		// Combo 4: Different discount rates AND different auth fees
		const cardRates = config.flat_tiered_card_specific_discount_rates;
		const transactionFees = config.flat_tiered_transaction_specific_fees;

		// Get rates from card-specific config or fallback to rate_config
		const creditRate =
			cardRates?.visa_mastercard_discover_credit ??
			rateConfig?.visa_mastercard_discover_credit?.discountRate ??
			2.9;
		const debitRate =
			cardRates?.visa_mastercard_discover_debit ??
			rateConfig?.visa_mastercard_discover_debit?.discountRate ??
			2.9;
		const amexRate =
			cardRates?.american_express ??
			rateConfig?.american_express?.discountRate ??
			3.75;
		const pinDebitRate =
			cardRates?.pin_debit ?? rateConfig?.pin_debit?.discountRate ?? 2.9;

		// Get authorization fees from transaction-specific config or fallback to rate_config
		const creditAuthFee =
			transactionFees?.credit_transactions ??
			rateConfig?.visa_mastercard_discover_credit?.authFee ??
			0.3;
		const debitAuthFee =
			transactionFees?.debit_transactions ??
			rateConfig?.visa_mastercard_discover_debit?.authFee ??
			0.3;
		const pinDebitAuthFee =
			transactionFees?.pin_debit_transactions ??
			rateConfig?.pin_debit?.authFee ??
			0.3;
		const amexAuthFee = rateConfig?.american_express?.authFee ?? 0.35;

		return {
			by_network: {
				// Credit networks use credit rates
				VISA: {
					discount_rate: creditRate,
					authorization_fee: creditAuthFee,
				},
				MASTERCARD: {
					discount_rate: creditRate,
					authorization_fee: creditAuthFee,
				},
				DISCOVER: {
					discount_rate: creditRate,
					authorization_fee: creditAuthFee,
				},
				AMERICAN_EXPRESS: {
					discount_rate: amexRate,
					authorization_fee: amexAuthFee,
				},
				// Debit networks use debit rates
				VISA_DEBIT: {
					discount_rate: debitRate,
					authorization_fee: debitAuthFee,
				},
				MASTERCARD_DEBIT: {
					discount_rate: debitRate,
					authorization_fee: debitAuthFee,
				},
				DISCOVER_DEBIT: {
					discount_rate: debitRate,
					authorization_fee: debitAuthFee,
				},
				PIN_DEBIT: {
					discount_rate: pinDebitRate,
					authorization_fee: pinDebitAuthFee,
				},
			},
		};
	}
}

/**
 * Fallback for when flat tiered config is not available
 */
function buildFallbackFlatTieredRatesConfig() {
	const rate = 2.9;
	const authFee = 0.3;

	return {
		by_network: {
			VISA: { discount_rate: rate, authorization_fee: authFee },
			MASTERCARD: { discount_rate: rate, authorization_fee: authFee },
			DISCOVER: { discount_rate: rate, authorization_fee: authFee },
			AMERICAN_EXPRESS: { discount_rate: rate, authorization_fee: authFee },
		},
	};
}

/**
 * Helper function to get authorization fee for a specific card type based on transaction type
 */
function getAuthFeeForCardType(
	cardType: string,
	config: PricingConfiguration,
): number {
	if (!config.transaction_specific_fees) {
		return config.standard_authorization_fee || 0.1;
	}

	// Map card types to transaction types
	if (
		['VISA', 'MASTERCARD', 'DISCOVER', 'AMERICAN_EXPRESS'].includes(cardType)
	) {
		return config.transaction_specific_fees.credit_transactions !== undefined
			? config.transaction_specific_fees.credit_transactions
			: config.standard_authorization_fee || 0.1;
	} else if (
		['VISA_DEBIT', 'MASTERCARD_DEBIT', 'DISCOVER_DEBIT'].includes(cardType)
	) {
		return config.transaction_specific_fees.debit_transactions !== undefined
			? config.transaction_specific_fees.debit_transactions
			: config.standard_authorization_fee || 0.08;
	} else if (cardType === 'PIN_DEBIT') {
		return config.transaction_specific_fees.pin_debit_transactions !== undefined
			? config.transaction_specific_fees.pin_debit_transactions
			: config.standard_authorization_fee || 0.15;
	} else if (['EBT', 'FLEET'].includes(cardType)) {
		// EBT and FLEET typically use credit transaction fees
		return config.transaction_specific_fees.credit_transactions !== undefined
			? config.transaction_specific_fees.credit_transactions
			: config.standard_authorization_fee || 0.1;
	}

	return config.standard_authorization_fee || 0.1;
}

/**
 * SIMPLIFIED: Build standard rates config directly from UI state
 * Uses IC_PLUS-specific fields when available for multi-rate-type support
 */
function buildSimpleStandardRatesConfig(config: PricingConfiguration) {
	// Use IC_PLUS-specific modes and rates when available
	const discountRateMode =
		config.ic_plus_discount_rate_mode ||
		config.discount_rate_mode ||
		'same_for_all';
	const authFeeMode =
		config.ic_plus_authorization_fee_mode ||
		config.authorization_fee_mode ||
		'same_for_all';
	const standardDiscountRate =
		config.ic_plus_standard_discount_rate ??
		config.standard_discount_rate ??
		0.65;
	const standardAuthFee =
		config.ic_plus_standard_authorization_fee ??
		config.standard_authorization_fee ??
		0.1;

	// Handle mixed modes: same discount rate + different auth fees
	if (discountRateMode === 'same_for_all' && authFeeMode === 'same_for_all') {
		return {
			mode: 'same_for_all',
			same_for_all: {
				discount_rate: standardDiscountRate,
				authorization_fee: standardAuthFee,
			} as any,
		};
	} else if (
		discountRateMode === 'same_for_all' &&
		authFeeMode === 'different_by_transaction_type'
	) {
		// Same discount rate but different auth fees - use different_by_card_type structure
		// Build rates dictionary for new structure
		const rates: Record<string, any> = {};

		// All cards get the same discount rate but different auth fees
		for (const cardType of [
			'VISA',
			'MASTERCARD',
			'DISCOVER',
			'AMERICAN_EXPRESS',
			'VISA_DEBIT',
			'MASTERCARD_DEBIT',
			'DISCOVER_DEBIT',
			'PIN_DEBIT',
			'EBT',
			'FLEET',
		]) {
			rates[cardType] = {
				discount_rate: standardDiscountRate,
				authorization_fee: getAuthFeeForCardType(cardType, config),
			};
		}

		return {
			mode: 'different_by_card_type',
			by_card_type: rates,
		};
	} else if (
		discountRateMode === 'different_by_card_type' &&
		authFeeMode === 'same_for_all'
	) {
		// Combo 3: Different discount rates per card, but SAME auth fee for all
		const rates: Record<string, any> = {};

		// Get IC_PLUS-specific card rates (since this function is for IC_PLUS)
		const cardRates =
			config.ic_plus_card_specific_discount_rates ||
			config.card_specific_discount_rates;

		// VISA, MASTERCARD, DISCOVER - different rates but same auth fee
		for (const cardType of ['VISA', 'MASTERCARD', 'DISCOVER']) {
			rates[cardType] = {
				discount_rate:
					cardRates?.visa_mastercard_discover_credit || standardDiscountRate,
				authorization_fee: standardAuthFee,
			};
		}

		// AMERICAN_EXPRESS - different rate but same auth fee
		rates.AMERICAN_EXPRESS = {
			discount_rate:
				cardRates?.american_express ||
				config.amex_standard_rates?.discount_rate ||
				0.7,
			authorization_fee: standardAuthFee,
		};

		// Individual debit cards - different rates but same auth fee
		rates.VISA_DEBIT = {
			discount_rate:
				cardRates?.visa_mastercard_discover_debit ||
				standardDiscountRate ||
				0.4,
			authorization_fee: standardAuthFee,
		};

		rates.MASTERCARD_DEBIT = {
			discount_rate:
				cardRates?.visa_mastercard_discover_debit ||
				standardDiscountRate ||
				0.42,
			authorization_fee: standardAuthFee,
		};

		rates.DISCOVER_DEBIT = {
			discount_rate:
				cardRates?.visa_mastercard_discover_debit ||
				standardDiscountRate ||
				0.4,
			authorization_fee: standardAuthFee,
		};

		rates.PIN_DEBIT = {
			discount_rate: cardRates?.pin_debit || standardDiscountRate || 0.7,
			authorization_fee: standardAuthFee,
		};

		rates.EBT = {
			authorization_fee: standardAuthFee,
		};

		rates.FLEET = {
			authorization_fee: standardAuthFee,
		};

		return {
			mode: 'different_by_card_type',
			by_card_type: rates,
		};
	} else {
		// Combo 4: Different discount rates AND different auth fees
		const rates: Record<string, any> = {};

		// Get IC_PLUS-specific card rates (since this function is for IC_PLUS)
		const cardRates =
			config.ic_plus_card_specific_discount_rates ||
			config.card_specific_discount_rates;
		const transactionFees =
			config.ic_plus_transaction_specific_fees ||
			config.transaction_specific_fees;

		// VISA, MASTERCARD, DISCOVER
		for (const cardType of ['VISA', 'MASTERCARD', 'DISCOVER']) {
			rates[cardType] = {
				discount_rate:
					cardRates?.visa_mastercard_discover_credit || standardDiscountRate,
				authorization_fee:
					transactionFees?.credit_transactions || standardAuthFee,
			};
		}

		// AMERICAN_EXPRESS
		rates.AMERICAN_EXPRESS = {
			discount_rate:
				cardRates?.american_express ||
				config.amex_standard_rates?.discount_rate ||
				0.7,
			authorization_fee:
				transactionFees?.credit_transactions ||
				config.amex_standard_rates?.authorization_fee ||
				0.15,
		};

		// Individual debit cards
		rates.VISA_DEBIT = {
			discount_rate:
				cardRates?.visa_mastercard_discover_debit ||
				standardDiscountRate ||
				0.4,
			authorization_fee:
				transactionFees?.debit_transactions || standardAuthFee || 0.08,
		};

		rates.MASTERCARD_DEBIT = {
			discount_rate:
				cardRates?.visa_mastercard_discover_debit ||
				standardDiscountRate ||
				0.42,
			authorization_fee:
				transactionFees?.debit_transactions || standardAuthFee || 0.08,
		};

		rates.DISCOVER_DEBIT = {
			discount_rate:
				cardRates?.visa_mastercard_discover_debit ||
				standardDiscountRate ||
				0.4,
			authorization_fee:
				transactionFees?.debit_transactions || standardAuthFee || 0.08,
		};

		rates.PIN_DEBIT = {
			discount_rate: cardRates?.pin_debit || standardDiscountRate || 0.7,
			authorization_fee:
				transactionFees?.pin_debit_transactions || standardAuthFee || 0.15,
		};

		rates.EBT = {
			authorization_fee:
				transactionFees?.credit_transactions || standardAuthFee || 0.05,
		};

		rates.FLEET = {
			authorization_fee:
				transactionFees?.credit_transactions || standardAuthFee || 0.12,
		};

		return {
			mode: 'different_by_card_type',
			by_card_type: rates,
		};
	}
}

/**
 * SIMPLIFIED: Transform enhanced API response to wizard state - DIRECT MAPPING
 */
export function transformEnhancedResponseToWizard(
	response: EnhancedCustomRatesResponse,
): PricingConfiguration {
	// Helper function to check if a rate type is selected
	const hasRateType = (rateType: string) => {
		const selectedTypes = response.selected_rate_types || [
			response.rate_plan_type,
		];
		return selectedTypes?.includes(rateType as any) || false;
	};

	const wizardConfig = {
		id: response.id,
		name: response.name,
		description: response.description || '',
		is_default: response.is_default,
		pricing_limits: response.pricing_limits,
		partner_id: response.partner_id ?? '',
		risk_tier: response.risk_tier ?? '',
		status: response.status ?? 'CUSTOM',
		rate_plan_type: response.rate_plan_type,
		selected_rate_types: response.selected_rate_types || [
			response.rate_plan_type,
		],
		discount_rate_mode: response.pricing_mode.discount_rate_mode,
		authorization_fee_mode: response.pricing_mode.authorization_fee_mode, // This will be overridden by rate-type-specific modes below
		pass_through_cost_mode: response.pricing_mode.pass_through_cost_mode,
		use_standard_assessment_rates:
			!response.pass_through_costs.use_custom_assessment_rates,
		custom_assessment_rate: response.pass_through_costs.custom_assessment_rate,
		use_bundled_amex_rate: response.pass_through_costs.use_bundled_amex_rate,
		bundled_amex_rate: response.pass_through_costs.bundled_amex_rate,
		pass_through_card_brand_fees: (response.pass_through_costs as any)
			.pass_through_card_brand_fees,
		pass_through_domestic_card_brand_fees: (response.pass_through_costs as any)
			.pass_through_domestic_card_brand_fees,
		pass_through_international_card_brand_fees: (
			response.pass_through_costs as any
		).pass_through_international_card_brand_fees,

		// UNIFIED: Single rate config that works for all rate types
		rate_config: extractUnifiedRateConfig(response),

		// Direct mapping from API response (for backward compatibility)
		rates: response.rates,
		same_as_credit_6tier:
			response.rates.tiered_rates?.same_for_all?.same_as_credit ?? false,

		flat_tiered_standard_discount_rate: hasRateType('FLAT_TIERED')
			? (() => {
					const flatTieredRates = response.rates.flat_tiered_rates;
					if (!flatTieredRates?.by_network) return undefined;

					const uniformRate = getUniformValue(
						flatTieredRates.by_network,
						ALL_NETWORKS,
						'discount_rate',
					);
					if (uniformRate !== undefined) {
						return uniformRate;
					}

					// Fallback: return VISA rate if available
					return flatTieredRates.by_network?.VISA?.discount_rate;
				})()
			: undefined,
		// FIX: Handle flat_tiered authorization fee - check if all networks have the same fee
		// When auth fee mode is same_for_all, extract the fee from any network (they're all the same)
		flat_tiered_standard_authorization_fee: hasRateType('FLAT_TIERED')
			? (() => {
					const flatTieredRates = response.rates.flat_tiered_rates;
					if (!flatTieredRates?.by_network) return undefined;

					// If we have any fees, check if they're all the same
					// If all fees are the same, use that value (regardless of authorization_fee_mode)
					// This handles cases where the mode might be incorrectly set but values are consistent
					const uniformFee = getUniformValue(
						flatTieredRates.by_network,
						ALL_NETWORKS,
						'authorization_fee',
					);
					if (uniformFee !== undefined) {
						return uniformFee;
					}

					// Fallback to VISA if not all same (for backward compatibility)
					return flatTieredRates.by_network.VISA?.authorization_fee;
				})()
			: undefined,

		// Extract card-specific discount rates for IC_PLUS/FLAT_TIERED different_by_card_type mode
		// FIX: Check the actual rate structure mode, not the global pricing_mode
		card_specific_discount_rates:
			response.rates.standard_rates?.mode === 'different_by_card_type' ||
			response.rates.flat_tiered_rates?.by_network
				? {
						visa_mastercard_discover_credit:
							(hasRateType('FLAT_TIERED')
								? response.rates.flat_tiered_rates?.by_network?.VISA
										?.discount_rate
								: response.rates.standard_rates?.by_card_type?.VISA
										?.discount_rate) || 0.65,
						visa_mastercard_discover_debit:
							response.rates.standard_rates?.by_card_type?.VISA_DEBIT
								?.discount_rate || 0.4,
						american_express:
							(hasRateType('FLAT_TIERED')
								? response.rates.flat_tiered_rates?.by_network?.AMERICAN_EXPRESS
										?.discount_rate
								: response.rates.standard_rates?.by_card_type?.AMERICAN_EXPRESS
										?.discount_rate) || 0.7,
						pin_debit:
							response.rates.standard_rates?.by_card_type?.PIN_DEBIT
								?.discount_rate || 0.7,
					}
				: undefined,

		// Rate-type-specific standard rates (for same_for_all mode)
		// FIX: Extract discount rate even when mode is different_by_card_type but all rates are the same
		ic_plus_standard_discount_rate: hasRateType('IC_PLUS')
			? (() => {
					const standardRates = response.rates.standard_rates;
					if (!standardRates) return undefined;

					if (standardRates.mode === 'same_for_all') {
						return (standardRates.same_for_all as any)?.discount_rate ?? 0.65;
					}

					// If mode is different_by_card_type, check if all discount rates are actually the same
					if (
						standardRates.mode === 'different_by_card_type' &&
						standardRates.by_card_type
					) {
						const uniformRate = getUniformValue(
							standardRates.by_card_type,
							ALL_CARD_TYPES,
							'discount_rate',
						);
						if (uniformRate !== undefined) {
							return uniformRate;
						}
					}

					return undefined;
				})()
			: undefined,
		// FIX: Handle authorization_fee_mode === 'same_for_all' even when mode === 'different_by_card_type'
		// When auth fee mode is same_for_all, extract the fee from any card type (they're all the same)
		// Also check if all card types have the same auth fee value (regardless of mode)
		ic_plus_standard_authorization_fee: hasRateType('IC_PLUS')
			? (() => {
					const standardRates = response.rates.standard_rates;
					if (!standardRates) return undefined;

					// If mode is same_for_all, get from same_for_all object
					if (standardRates.mode === 'same_for_all') {
						return (
							(standardRates.same_for_all as any)?.authorization_fee ?? 0.1
						);
					}

					// If mode is different_by_card_type, check if all cards have the same auth fee
					// This handles the case where discount rates differ by card type but auth fees are the same
					if (
						standardRates.mode === 'different_by_card_type' &&
						standardRates.by_card_type
					) {
						// If all fees are the same, use that value (regardless of authorization_fee_mode)
						// This handles cases where the mode might be incorrectly set but values are consistent
						const uniformFee = getUniformValue(
							standardRates.by_card_type,
							ALL_CARD_TYPES,
							'authorization_fee',
						);
						if (uniformFee !== undefined) {
							return uniformFee;
						}
					}

					return undefined;
				})()
			: undefined,

		// Rate-type-specific card discount rates
		ic_plus_card_specific_discount_rates:
			hasRateType('IC_PLUS') &&
			response.rates.standard_rates?.mode === 'different_by_card_type'
				? {
						visa_mastercard_discover_credit:
							response.rates.standard_rates?.by_card_type?.VISA
								?.discount_rate || 0.65,
						visa_mastercard_discover_debit:
							response.rates.standard_rates?.by_card_type?.VISA_DEBIT
								?.discount_rate || 0.4,
						american_express:
							response.rates.standard_rates?.by_card_type?.AMERICAN_EXPRESS
								?.discount_rate || 0.7,
						pin_debit:
							response.rates.standard_rates?.by_card_type?.PIN_DEBIT
								?.discount_rate || 0.7,
					}
				: undefined,

		tiered_card_specific_discount_rates:
			hasRateType('TIERED') &&
			response.pricing_mode.discount_rate_mode === 'different_by_card_type'
				? {
						visa_mastercard_discover_credit:
							response.rates.tiered_rates?.same_for_all?.credit_rates
								?.qualified_rate || 0.65,
						visa_mastercard_discover_debit:
							response.rates.tiered_rates?.same_for_all?.debit_rates
								?.qualified_rate || 0.4,
						american_express:
							response.rates.tiered_rates?.by_card_type?.AMERICAN_EXPRESS
								?.tiered?.qualified_rate || 0.7,
						pin_debit:
							response.rates.tiered_rates?.by_card_type?.PIN_DEBIT?.tiered
								?.qualified_rate || 0.7,
					}
				: undefined,

		flat_tiered_card_specific_discount_rates:
			hasRateType('FLAT_TIERED') &&
			(() => {
				const flatTieredRates = response.rates.flat_tiered_rates;
				if (!flatTieredRates?.by_network) return false;
				const byNetwork = flatTieredRates.by_network as any;
				const visaRate = byNetwork.VISA?.discount_rate;
				const mastercardRate = byNetwork.MASTERCARD?.discount_rate;
				const amexRate = byNetwork.AMERICAN_EXPRESS?.discount_rate;
				return (
					visaRate &&
					((mastercardRate && Math.abs(visaRate - mastercardRate) >= 0.001) ||
						(amexRate && Math.abs(visaRate - amexRate) >= 0.001))
				);
			})()
				? {
						visa_mastercard_discover_credit:
							(response.rates.flat_tiered_rates?.by_network as any)?.VISA
								?.discount_rate || 2.9,
						visa_mastercard_discover_debit:
							(response.rates.flat_tiered_rates?.by_network as any)?.VISA_DEBIT
								?.discount_rate || 2.9,
						american_express:
							(response.rates.flat_tiered_rates?.by_network as any)
								?.AMERICAN_EXPRESS?.discount_rate || 3.75,
						pin_debit:
							(response.rates.flat_tiered_rates?.by_network as any)?.PIN_DEBIT
								?.discount_rate || 2.9,
					}
				: undefined,

		// Extract transaction-specific fees for IC_PLUS/FLAT_TIERED different_by_transaction_type mode
		// FIX: Detect from actual data structure - if standard_rates has by_card_type with different auth fees
		transaction_specific_fees: (() => {
			const standardRates = response.rates.standard_rates;
			if (
				standardRates?.mode === 'different_by_card_type' &&
				standardRates.by_card_type
			) {
				const visaFee = standardRates.by_card_type.VISA?.authorization_fee;
				const visaDebitFee =
					standardRates.by_card_type.VISA_DEBIT?.authorization_fee;
				if (
					visaFee &&
					visaDebitFee &&
					Math.abs(visaFee - visaDebitFee) > 0.001
				) {
					return true; // Different fees by transaction type
				}
			}
			return (
				response.pricing_mode.authorization_fee_mode ===
				'different_by_transaction_type'
			);
		})()
			? {
					credit_transactions:
						(hasRateType('FLAT_TIERED')
							? response.rates.flat_tiered_rates?.by_network?.VISA
									?.authorization_fee
							: response.rates.standard_rates?.by_card_type?.VISA
									?.authorization_fee) || 0.1,
					debit_transactions:
						response.rates.standard_rates?.by_card_type?.VISA_DEBIT
							?.authorization_fee || 0.08,
					pin_debit_transactions:
						response.rates.standard_rates?.by_card_type?.PIN_DEBIT
							?.authorization_fee || 0.15,
				}
			: undefined,

		// Rate-type-specific transaction fees
		// FIX: Detect from actual data structure
		ic_plus_transaction_specific_fees:
			hasRateType('IC_PLUS') &&
			(() => {
				const standardRates = response.rates.standard_rates;
				if (
					standardRates?.mode === 'different_by_card_type' &&
					standardRates.by_card_type
				) {
					const visaFee = standardRates.by_card_type.VISA?.authorization_fee;
					const visaDebitFee =
						standardRates.by_card_type.VISA_DEBIT?.authorization_fee;
					if (
						visaFee &&
						visaDebitFee &&
						Math.abs(visaFee - visaDebitFee) > 0.001
					) {
						return true; // Different fees by transaction type
					}
				}
				return (
					response.pricing_mode.authorization_fee_mode ===
					'different_by_transaction_type'
				);
			})()
				? {
						credit_transactions:
							response.rates.standard_rates?.by_card_type?.VISA
								?.authorization_fee || 0.1,
						debit_transactions:
							response.rates.standard_rates?.by_card_type?.VISA_DEBIT
								?.authorization_fee || 0.08,
						pin_debit_transactions:
							response.rates.standard_rates?.by_card_type?.PIN_DEBIT
								?.authorization_fee || 0.15,
					}
				: undefined,

		tiered_transaction_specific_fees:
			hasRateType('TIERED') &&
			response.pricing_mode.authorization_fee_mode ===
				'different_by_transaction_type'
				? {
						credit_transactions:
							response.rates.tiered_rates?.same_for_all?.credit_rates
								?.qualified_per_item || 0.1,
						debit_transactions:
							response.rates.tiered_rates?.same_for_all?.debit_rates
								?.qualified_per_item || 0.08,
						pin_debit_transactions:
							response.rates.tiered_rates?.by_card_type?.PIN_DEBIT?.tiered
								?.qualified_per_item || 0.15,
					}
				: undefined,

		flat_tiered_transaction_specific_fees:
			hasRateType('FLAT_TIERED') &&
			(() => {
				const flatTieredRates = response.rates.flat_tiered_rates;
				if (!flatTieredRates?.by_network)
					return (
						response.pricing_mode.authorization_fee_mode ===
						'different_by_transaction_type'
					);
				const byNetwork = flatTieredRates.by_network as any;
				const visaFee = byNetwork.VISA?.authorization_fee;
				const visaDebitFee = byNetwork.VISA_DEBIT?.authorization_fee;
				if (
					visaFee &&
					visaDebitFee &&
					Math.abs(visaFee - visaDebitFee) > 0.001
				) {
					return true; // Different fees by transaction type
				}
				return (
					response.pricing_mode.authorization_fee_mode ===
					'different_by_transaction_type'
				);
			})()
				? {
						credit_transactions:
							(response.rates.flat_tiered_rates?.by_network as any)?.VISA
								?.authorization_fee || 0.3,
						debit_transactions:
							(response.rates.flat_tiered_rates?.by_network as any)?.VISA_DEBIT
								?.authorization_fee || 0.3,
						pin_debit_transactions:
							(response.rates.flat_tiered_rates?.by_network as any)?.PIN_DEBIT
								?.authorization_fee || 0.3,
					}
				: undefined,

		// Rate-type-specific authorization fee modes
		// FIX: Detect IC+ auth fee mode from the actual data structure
		// If standard_rates.mode is 'same_for_all', check if all cards have same auth fee
		// If standard_rates.mode is 'different_by_card_type', check if auth fees differ by transaction type
		ic_plus_authorization_fee_mode: hasRateType('IC_PLUS')
			? (() => {
					const standardRates = response.rates.standard_rates;
					if (!standardRates)
						return response.pricing_mode.authorization_fee_mode;

					if (standardRates.mode === 'same_for_all') {
						// All cards have same rates, so auth fee is same_for_all
						return 'same_for_all';
					} else if (
						standardRates.mode === 'different_by_card_type' &&
						standardRates.by_card_type
					) {
						// Check if auth fees differ by transaction type (credit vs debit)
						const visaFee = standardRates.by_card_type.VISA?.authorization_fee;
						const visaDebitFee =
							standardRates.by_card_type.VISA_DEBIT?.authorization_fee;
						const pinDebitFee =
							standardRates.by_card_type.PIN_DEBIT?.authorization_fee;

						// If debit/pin debit fees are different from credit fee, it's different_by_transaction_type
						if (
							visaFee &&
							visaDebitFee &&
							Math.abs(visaFee - visaDebitFee) > 0.001
						) {
							return 'different_by_transaction_type';
						}
						if (
							visaFee &&
							pinDebitFee &&
							Math.abs(visaFee - pinDebitFee) > 0.001
						) {
							return 'different_by_transaction_type';
						}
						// All fees are the same, so it's same_for_all (but mode is different_by_card_type due to different discount rates)
						return 'same_for_all';
					}
					return response.pricing_mode.authorization_fee_mode;
				})()
			: undefined,
		tiered_authorization_fee_mode: hasRateType('TIERED')
			? response.pricing_mode.authorization_fee_mode
			: undefined,
		// FIX: Detect Flat Tiered auth fee mode from the actual data structure
		flat_tiered_authorization_fee_mode: hasRateType('FLAT_TIERED')
			? (() => {
					// First check if there's a separate field
					if ((response as any).flat_tiered_authorization_fee_mode) {
						return (response as any).flat_tiered_authorization_fee_mode;
					}

					const flatTieredRates = response.rates.flat_tiered_rates;
					if (!flatTieredRates?.by_network)
						return response.pricing_mode.authorization_fee_mode;

					// Check if all networks have the same auth fee
					const byNetwork = flatTieredRates.by_network as any;
					const visaFee = byNetwork.VISA?.authorization_fee;
					const visaDebitFee = byNetwork.VISA_DEBIT?.authorization_fee;
					const pinDebitFee = byNetwork.PIN_DEBIT?.authorization_fee;

					if (
						visaFee &&
						visaDebitFee &&
						Math.abs(visaFee - visaDebitFee) > 0.001
					) {
						return 'different_by_transaction_type';
					}
					if (
						visaFee &&
						pinDebitFee &&
						Math.abs(visaFee - pinDebitFee) > 0.001
					) {
						return 'different_by_transaction_type';
					}

					return 'same_for_all';
				})()
			: undefined,

		// Rate-type-specific discount rate modes (read directly from their respective rate structures)
		// FIX: Detect IC+ discount rate mode from actual data - check if all rates are the same
		// Even if mode is 'different_by_card_type', if all discount rates are the same, it's 'same_for_all'
		ic_plus_discount_rate_mode: hasRateType('IC_PLUS')
			? (() => {
					const standardRates = response.rates.standard_rates;
					if (!standardRates) return response.pricing_mode.discount_rate_mode;

					// If mode is same_for_all, return that
					if (standardRates.mode === 'same_for_all') {
						return 'same_for_all';
					}

					// If mode is different_by_card_type, check if all discount rates are actually the same
					if (
						standardRates.mode === 'different_by_card_type' &&
						standardRates.by_card_type
					) {
						// If all rates are the same (within 0.001 tolerance), it's same_for_all
						const uniformRate = getUniformValue(
							standardRates.by_card_type,
							ALL_CARD_TYPES,
							'discount_rate',
						);
						if (uniformRate !== undefined) {
							return 'same_for_all';
						}

						// Rates are actually different, so return 'different_by_card_type'
						return 'different_by_card_type';
					}

					// Fallback to mode or pricing_mode
					return standardRates.mode || response.pricing_mode.discount_rate_mode;
				})()
			: undefined,
		// FIX: Read tiered mode directly from tiered_rates.mode, not from global pricing_mode
		tiered_discount_rate_mode: hasRateType('TIERED')
			? response.rates.tiered_rates?.mode ||
				response.pricing_mode.discount_rate_mode
			: undefined,
		// FIX: Detect Flat Tiered discount rate mode from the actual data structure
		flat_tiered_discount_rate_mode: hasRateType('FLAT_TIERED')
			? (() => {
					const flatTieredRates = response.rates.flat_tiered_rates;
					if (!flatTieredRates?.by_network)
						return response.pricing_mode.discount_rate_mode;

					// Check if all networks have the same discount rate
					const byNetwork = flatTieredRates.by_network as any;
					const visaRate = byNetwork.VISA?.discount_rate;
					const mastercardRate = byNetwork.MASTERCARD?.discount_rate;
					const amexRate = byNetwork.AMERICAN_EXPRESS?.discount_rate;
					const visaDebitRate = byNetwork.VISA_DEBIT?.discount_rate;

					if (!visaRate) return response.pricing_mode.discount_rate_mode;

					// If all rates are the same (within 0.001 tolerance), it's same_for_all
					const allSame =
						(!mastercardRate || Math.abs(visaRate - mastercardRate) < 0.001) &&
						(!amexRate || Math.abs(visaRate - amexRate) < 0.001) &&
						(!visaDebitRate || Math.abs(visaRate - visaDebitRate) < 0.001);

					return allSame ? 'same_for_all' : 'different_by_card_type';
				})()
			: undefined,

		// Tiered-specific same as above flag
		// FIX: Check top-level same_as_credit first (for different_by_card_type mode), then fall back to same_for_all
		tiered_same_as_credit_6tier:
			response.rates.tiered_rates?.same_as_credit ??
			response.rates.tiered_rates?.same_for_all?.same_as_credit ??
			false,

		// Required field
		custom_rates: {} as any,

		// Extract individual card rates for UI
		credit_rates:
			response.rates.tiered_rates?.by_card_type?.VISA?.credit_only ||
			response.rates.tiered_rates?.same_for_all?.credit_rates, // Fallback for same_for_all mode
		debit_rates:
			response.rates.tiered_rates?.by_card_type?.VISA?.with_debit ||
			response.rates.tiered_rates?.same_for_all?.debit_rates, // Fallback for same_for_all mode
		amex_rates:
			response.rates.tiered_rates?.by_card_type?.AMERICAN_EXPRESS?.tiered ||
			response.rates.tiered_rates?.same_for_all?.credit_rates, // Fallback for same_for_all mode
		visa_debit_rates:
			response.rates.tiered_rates?.by_card_type?.VISA_DEBIT?.tiered ||
			response.rates.tiered_rates?.same_for_all?.debit_rates, // Fallback for same_for_all mode
		mastercard_debit_rates:
			response.rates.tiered_rates?.by_card_type?.MASTERCARD_DEBIT?.tiered ||
			response.rates.tiered_rates?.same_for_all?.debit_rates, // Fallback for same_for_all mode
		discover_debit_rates:
			response.rates.tiered_rates?.by_card_type?.DISCOVER_DEBIT?.tiered ||
			response.rates.tiered_rates?.same_for_all?.debit_rates, // Fallback for same_for_all mode
		pin_debit_rates: response.rates.tiered_rates?.by_card_type?.PIN_DEBIT
			?.tiered || {
			qualified_rate: 1.85,
			qualified_per_item: 0.1,
			midqualified_rate: 2.5,
			midqualified_per_item: 0.1,
			nonqualified_rate: 2.85,
			nonqualified_per_item: 0.1,
		}, // Default PIN debit rates

		// Individual card rates for different_by_card_type mode
		visa_rates: response.rates.tiered_rates?.by_card_type?.VISA || undefined,
		mastercard_rates:
			response.rates.tiered_rates?.by_card_type?.MASTERCARD || undefined,
		discover_rates:
			response.rates.tiered_rates?.by_card_type?.DISCOVER || undefined,

		// Individual standard rates for IC_PLUS/FLAT different_by_card_type mode
		visa_standard_rates:
			response.rates.standard_rates?.by_card_type?.VISA || undefined,
		mastercard_standard_rates:
			response.rates.standard_rates?.by_card_type?.MASTERCARD || undefined,
		discover_standard_rates:
			response.rates.standard_rates?.by_card_type?.DISCOVER || undefined,
		amex_standard_rates:
			response.rates.standard_rates?.by_card_type?.AMERICAN_EXPRESS ||
			undefined,
		visa_debit_standard_rates:
			response.rates.standard_rates?.by_card_type?.VISA_DEBIT || undefined,
		mastercard_debit_standard_rates:
			response.rates.standard_rates?.by_card_type?.MASTERCARD_DEBIT ||
			undefined,
		discover_debit_standard_rates:
			response.rates.standard_rates?.by_card_type?.DISCOVER_DEBIT || undefined,
		pin_debit_standard_rates:
			response.rates.standard_rates?.by_card_type?.PIN_DEBIT || undefined,

		// Monthly and Batch Fees
		monthly_fees: response.monthly_and_batch_fees?.monthly_fees || [],
		batch_fee: response.monthly_and_batch_fees?.batch_fee || 0,
		batch_count_per_month:
			response.monthly_and_batch_fees?.batch_count_per_month || 30,
		gateway_fee: response.monthly_and_batch_fees?.gateway_fee || 0,
		equipment_fee: response.monthly_and_batch_fees?.equipment_fee || 0,
		surcharge_rates: response.rates.surcharge_rates,

		created_at: response.created_at,
		updated_at: response.updated_at,
		created_by: response.created_by,
		updated_by: response.updated_by,
		enterprise_id: response.enterprise_id,
	};

	return wizardConfig;
}

/**
 * SIMPLIFIED: Create default pricing configuration using backend defaults
 */
export function createDefaultPricingConfiguration(): PricingConfiguration {
	return {
		name: '',
		description: '',
		is_default: true,
		rate_plan_type: 'IC_PLUS',
		discount_rate_mode: 'same_for_all',
		authorization_fee_mode: 'same_for_all',
		pass_through_cost_mode: 'standard',
		use_standard_assessment_rates: true,
		custom_assessment_rate: undefined,
		use_bundled_amex_rate: false,
		bundled_amex_rate: undefined,

		// UNIFIED: Default rate config for all rate types
		rate_config: {
			mode: 'same_for_all',
			standardRate: 2.9,
			standardAuthFee: 0.3,
			visa_mastercard_discover_credit: { discountRate: 2.9, authFee: 0.3 },
			visa_mastercard_discover_debit: { discountRate: 2.9, authFee: 0.3 },
			american_express: { discountRate: 2.9, authFee: 0.3 },
			pin_debit: { discountRate: 2.9, authFee: 0.3 },
		},

		// Rate-type-specific modes (default to same_for_all for independence)
		ic_plus_discount_rate_mode: 'same_for_all',
		ic_plus_authorization_fee_mode: 'same_for_all',
		tiered_discount_rate_mode: 'same_for_all',
		tiered_authorization_fee_mode: 'same_for_all',
		tiered_same_as_credit_6tier: true,
		flat_tiered_discount_rate_mode: 'same_for_all',
		flat_tiered_authorization_fee_mode: 'same_for_all',

		// Use default rates from backend
		rates: {
			standard_rates: {
				mode: 'same_for_all',
				same_for_all: {
					rates: {
						discount_rate: 0.65, // 0.65% as decimal
						authorization_fee: 0.1,
					},
				},
			},
			tiered_rates: undefined,
		},

		same_as_credit_6tier: true,
		standard_discount_rate: 1.65,
		standard_authorization_fee: 0.91,

		// Required field
		custom_rates: {} as any,

		// Direct mapping from rates
		credit_rates: {
			qualified_rate: 1.65,
			qualified_per_item: 0.91,
			midqualified_rate: 1.75,
			midqualified_per_item: 0.91,
			nonqualified_rate: 2.25,
			nonqualified_per_item: 0.91,
		},
		debit_rates: {
			qualified_rate: 1.6,
			qualified_per_item: 0.79,
			midqualified_rate: 1.7,
			midqualified_per_item: 0.79,
			nonqualified_rate: 2.2,
			nonqualified_per_item: 0.79,
		},
		amex_rates: {
			qualified_rate: 1.85,
			qualified_per_item: 0.1,
			midqualified_rate: 2.5,
			midqualified_per_item: 0.1,
			nonqualified_rate: 2.85,
			nonqualified_per_item: 0.1,
		},
		visa_debit_rates: {
			qualified_rate: 1.6,
			qualified_per_item: 0.79,
			midqualified_rate: 1.7,
			midqualified_per_item: 0.79,
			nonqualified_rate: 2.2,
			nonqualified_per_item: 0.79,
		},
		mastercard_debit_rates: {
			qualified_rate: 1.6,
			qualified_per_item: 0.79,
			midqualified_rate: 1.7,
			midqualified_per_item: 0.79,
			nonqualified_rate: 2.2,
			nonqualified_per_item: 0.79,
		},
		discover_debit_rates: {
			qualified_rate: 1.6,
			qualified_per_item: 0.79,
			midqualified_rate: 1.7,
			midqualified_per_item: 0.79,
			nonqualified_rate: 2.2,
			nonqualified_per_item: 0.79,
		},
		pin_debit_rates: {
			qualified_rate: 1.85,
			qualified_per_item: 0.1,
			midqualified_rate: 2.5,
			midqualified_per_item: 0.1,
			nonqualified_rate: 2.85,
			nonqualified_per_item: 0.1,
		},
	};
}

/**
 * Extract unified rate config from API response
 */
function extractUnifiedRateConfig(
	response: EnhancedCustomRatesResponse,
): UnifiedRateConfig {
	// Determine mode from pricing_mode
	const mode =
		response.pricing_mode.discount_rate_mode === 'different_by_card_type'
			? 'different_by_card_type'
			: 'same_for_all';

	// Extract standard rates (when mode is same_for_all)
	const standardRate =
		response.rates.flat_tiered_rates?.by_network?.VISA?.discount_rate ||
		(response.rates.standard_rates?.same_for_all as any)?.discount_rate ||
		2.9;
	const standardAuthFee =
		response.rates.flat_tiered_rates?.by_network?.VISA?.authorization_fee ||
		(response.rates.standard_rates?.same_for_all as any)?.authorization_fee ||
		0.3;

	// Extract card-specific rates (when mode is different_by_card_type)
	const visaRate =
		response.rates.flat_tiered_rates?.by_network?.VISA?.discount_rate ||
		response.rates.standard_rates?.by_card_type?.VISA?.discount_rate ||
		2.9;
	const visaAuthFee =
		response.rates.flat_tiered_rates?.by_network?.VISA?.authorization_fee ||
		response.rates.standard_rates?.by_card_type?.VISA?.authorization_fee ||
		0.3;

	const amexRate =
		response.rates.flat_tiered_rates?.by_network?.AMERICAN_EXPRESS
			?.discount_rate ||
		response.rates.standard_rates?.by_card_type?.AMERICAN_EXPRESS
			?.discount_rate ||
		2.9;
	const amexAuthFee =
		response.rates.flat_tiered_rates?.by_network?.AMERICAN_EXPRESS
			?.authorization_fee ||
		response.rates.standard_rates?.by_card_type?.AMERICAN_EXPRESS
			?.authorization_fee ||
		0.3;

	return {
		mode,
		standardRate,
		standardAuthFee,
		visa_mastercard_discover_credit: {
			discountRate: visaRate,
			authFee: visaAuthFee,
		},
		visa_mastercard_discover_debit: {
			discountRate: visaRate,
			authFee: visaAuthFee,
		},
		american_express: { discountRate: amexRate, authFee: amexAuthFee },
		pin_debit: { discountRate: amexRate, authFee: amexAuthFee }, // Using AMEX as fallback for PIN
	};
}
